import t from "@/lang/lang";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import { CustomerAddressCard } from "@/services/backend/addresses/components/customer-address-card";
import { ReadOnlyAddressCard } from "@/services/backend/addresses/components/read-only-address-card";
import { CustomerAddress } from "@/services/backend/customers/types/customer";
import {
  PutCustomerAddressRequest,
  useCustomersCustomerAddressPutMutation,
  useCustomersCustomerAddressShowQuery,
} from "@/services/backend/customers/api/customer-api";
import { customerAddressIsEditable } from "@/services/backend/customers/lib/customer-address-editable";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardSkeleton } from "@/shared/components/card-skeleton";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { useState } from "react";
import { useParams } from "react-router";

export function CustomerAddressRoute() {
  const { customerId } = useParams();
  const canEditAddress =
    useFeatureAuth("gsm.customers.customer.create") &&
    customerAddressIsEditable();

  const {
    data: address,
    error,
    isLoading,
  } = useCustomersCustomerAddressShowQuery(
    {
      customerId: customerId ?? "",
    },
    {
      skip: !customerId,
    },
  );

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/address`,
              label: t("Kundenadresse"),
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent className="gap-4">
        {error && <RTKQueryErrorAlert error={error} />}
        {isLoading && <CardSkeleton />}
        {address && canEditAddress && (
          <PersistenceContextProvider>
            <EditableAddressData customerAddress={address} />
          </PersistenceContextProvider>
        )}
        {address && !canEditAddress && (
          <ReadOnlyAddressCard
            address={address.address}
            title={t("Kundenadresse")}
            description={t(
              "Die rechtsgültige Anschrift des Unternehmens. Sie wird in Dynamics Sales gepflegt und kann hier nicht bearbeitet werden.",
            )}
          />
        )}
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function EditableAddressData({
  customerAddress,
}: {
  customerAddress: CustomerAddress;
}) {
  const [request, setRequest] = useState<PutCustomerAddressRequest>({
    customerId: customerAddress.customerId,
    address: customerAddress.address,
  });
  const mutation = useCustomersCustomerAddressPutMutation();
  useDebouncedMutation(request, setRequest, mutation, {
    customerId: customerAddress.customerId,
    address: customerAddress.address,
  });

  return (
    <>
      <PersistenceStateUsingContext className="ml-auto inline-flex" />
      <CustomerAddressCard
        customerAddress={request.address}
        onCustomerAddressChange={(address) =>
          setRequest({ ...request, address })
        }
        description={t("Die rechtsgültige Anschrift des Unternehmens")}
      />
    </>
  );
}
